/**
 * CreditUsageHistory Component
 *
 * Displays a list of recent AI generations that consumed credits.
 * Shows date, client name, and room type for each generation.
 */

import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import { useLeadsWithGenerationsCompat } from '@/hooks/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Image as ImageIcon, Calendar, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ROOM_TYPE_LABELS: Record<string, string> = {
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  basement: 'Basement',
  living_room: 'Living Room',
  bedroom: 'Bedroom',
  outdoor: 'Outdoor',
  garage: 'Garage',
  home_office: 'Home Office',
  other: 'Other',
};

export function CreditUsageHistory() {
  const { tenantId } = useAuthContext();
  const { data: leads, isLoading } = useLeadsWithGenerationsCompat(tenantId ?? undefined);

  // Filter leads that have generated images (meaning they consumed credits)
  const generatedLeads = leads?.filter((lead) => {
    // Check if lead has generations
    return lead.generations && lead.generations.length > 0;
  }).slice(0, 10) || []; // Show latest 10

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Recent Credit Usage
          </CardTitle>
          <CardDescription>View your recent AI generation activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (generatedLeads.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Recent Credit Usage
          </CardTitle>
          <CardDescription>View your recent AI generation activity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border-2 border-dashed border-muted py-8 text-center">
            <ImageIcon className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No generations yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Credit usage will appear here after your first AI generation
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Recent Credit Usage
        </CardTitle>
        <CardDescription>
          Your latest {generatedLeads.length} AI generation{generatedLeads.length !== 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {generatedLeads.map((lead) => {
            const roomType = lead.gemini_analysis?.room_type || lead.room_type || 'other';
            const roomLabel = ROOM_TYPE_LABELS[roomType] || 'Unknown';

            return (
              <div
                key={lead.id}
                className="flex items-center justify-between rounded-lg border bg-card p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="h-3 w-3 text-muted-foreground shrink-0" />
                      <p className="text-sm font-medium truncate">
                        {lead.client_name}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {roomLabel}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                  <Calendar className="h-3 w-3" />
                  <span>
                    {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default CreditUsageHistory;
